import { isValid } from "date-fns";
import {
  storedPictureSearchConditionSchema,
  type StoredPictureSearchFormData,
} from "../components/storedPictureSearchConditionSchema";
import {
  STORED_PICTURE_ACQUIRER_OPTIONS,
  type StoredPictureAcquirer,
} from "./storedPictureSearch";

/**
 * 取得済商業登記の検索状態（検索条件・表示タブ・取得ユーザー）を sessionStorage で保存・復元する。
 * 詳細画面へ遷移して一覧へ戻った際に、直前の検索条件・タブ・結果表示を復元するために使う。
 *
 * 検証用に検索条件スキーマ（storedPictureSearchConditionSchema）へ依存するため、
 * schema が util（storedPictureSearch）へ依存する循環を避ける目的で、保存・復元だけを
 * この独立モジュールに切り出している（schema → util の一方向のみに保つ）。
 */

/** 一覧タブの種類（保存対象。CommercialBookList 側の型と一致させる） */
export type StoredPictureListTab = "request" | "picture";

/**
 * 検索条件・表示タブの保存キー。
 * sessionStorage を使うのでタブを閉じると消える（他ブラウザタブ・再訪には引き継がない）。
 */
export const STORED_PICTURE_SEARCH_SESSION_KEY =
  "commercialBook.storedPictureSearchState";

/**
 * 保存した利用者と共有設定。別の利用者や、共有設定が変わった後には復元しない
 * （共有OFFで「他ユーザー取得」を復元すると、選択欄が出ないまま0件になるため）
 */
export interface StoredPictureSearchIdentity {
  userId: string;
  isSharable: boolean;
}

/** sessionStorage に保存する検索状態。日付は JSON 化のため ISO 文字列で持つ */
interface PersistedSearchState extends StoredPictureSearchIdentity {
  condition: Omit<StoredPictureSearchFormData, "acquiredDateRange"> & {
    acquiredDateRange: [string | null, string | null];
  };
  tab: StoredPictureListTab;
  acquirer: StoredPictureAcquirer;
}

/** 復元した検索状態 */
export interface RestoredStoredPictureSearchState {
  condition: StoredPictureSearchFormData;
  tab: StoredPictureListTab;
  acquirer: StoredPictureAcquirer;
}

/**
 * 検索条件・表示タブ・取得ユーザーを sessionStorage に保存する。
 * 詳細画面へ遷移して一覧へ戻った際に、同じ条件・タブを復元するために使う。
 * @param condition 検索フォームの値
 * @param tab 表示中のタブ
 * @param acquirer 取得ユーザーの選択
 * @param identity 保存した利用者と共有設定
 */
export const saveStoredPictureSearchState = (
  condition: StoredPictureSearchFormData,
  tab: StoredPictureListTab,
  acquirer: StoredPictureAcquirer,
  identity: StoredPictureSearchIdentity
): void => {
  try {
    const [from, to] = condition.acquiredDateRange;
    const persisted: PersistedSearchState = {
      condition: {
        ...condition,
        acquiredDateRange: [
          from !== null && isValid(from) ? from.toISOString() : null,
          to !== null && isValid(to) ? to.toISOString() : null,
        ],
      },
      tab,
      acquirer,
      ...identity,
    };
    sessionStorage.setItem(
      STORED_PICTURE_SEARCH_SESSION_KEY,
      JSON.stringify(persisted)
    );
  } catch {
    // sessionStorage 不可（プライベートモード等）でも検索自体は続行できるため握りつぶす
  }
};

/** 保存済みの検索状態を消す（クリア押下時など） */
export const clearStoredPictureSearchState = (): void => {
  try {
    sessionStorage.removeItem(STORED_PICTURE_SEARCH_SESSION_KEY);
  } catch {
    // 削除失敗は無視する（保存不可環境でも動作を止めない）
  }
};

/**
 * sessionStorage から検索状態を復元する。
 * 保存が無い・壊れている・スキーマに合わない・利用者か共有設定が違う場合は null を返し、初期状態で開始する。
 * @param identity 現在の利用者と共有設定
 * @returns 復元した検索条件・表示タブ・取得ユーザー、または null
 */
export const loadStoredPictureSearchState = (
  identity: StoredPictureSearchIdentity
): RestoredStoredPictureSearchState | null => {
  try {
    const raw = sessionStorage.getItem(STORED_PICTURE_SEARCH_SESSION_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as PersistedSearchState;
    if (
      parsed.userId !== identity.userId ||
      parsed.isSharable !== identity.isSharable
    )
      return null;
    const [from, to] = parsed.condition?.acquiredDateRange ?? [null, null];
    const reviveDate = (value: string | null): Date | null => {
      if (value === null) return null;
      const date = new Date(value);
      return isValid(date) ? date : null;
    };
    // 日付を Date へ戻したうえでスキーマ検証する（型・必須条件を満たさない保存は破棄）
    const revived = {
      ...parsed.condition,
      acquiredDateRange: [reviveDate(from), reviveDate(to)] as [
        Date | null,
        Date | null
      ],
    };
    const result = storedPictureSearchConditionSchema.safeParse(revived);
    if (!result.success) return null;
    const tab: StoredPictureListTab =
      parsed.tab === "picture" ? "picture" : "request";
    const acquirer =
      STORED_PICTURE_ACQUIRER_OPTIONS.find(
        (option) => option.value === parsed.acquirer
      )?.value ?? "all";
    return { condition: result.data, tab, acquirer };
  } catch {
    return null;
  }
};
